import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const HeroImageManager = () => {
  const [heroImages, setHeroImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchHeroImages();
  }, []);

  const fetchHeroImages = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        ?.from('hero_images')
        ?.select('*')
        ?.order('display_order', { ascending: true });

      if (error) throw error;

      setHeroImages(data || []);
    } catch (err) {
      console.error('Error fetching hero images:', err);
      setError('Failed to load hero images');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file?.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      // Generate unique filename
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}_${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('hero-images')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      setUploadProgress(50);

      // Get public URL
      const { data: { publicUrl } } = supabase?.storage
        ?.from('hero-images')
        ?.getPublicUrl(filePath);

      setUploadProgress(75);

      // Get current user
      const { data: { user } } = await supabase?.auth?.getUser();

      // Calculate next display order
      const maxOrder = heroImages?.length > 0 
        ? Math.max(...heroImages?.map(img => img?.display_order || 0))
        : 0;

      // Insert record into hero_images table
      const { data: insertData, error: insertError } = await supabase
        ?.from('hero_images')
        ?.insert([
          {
            image_url: publicUrl,
            display_order: maxOrder + 1,
            is_active: true,
            uploaded_by: user?.id
          }
        ])
        ?.select()
        ?.single();

      if (insertError) throw insertError;

      setUploadProgress(100);
      setSuccess('Hero image uploaded successfully!');
      
      // Refresh the list
      await fetchHeroImages();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

      // Reset file input
      event.target.value = '';
    } catch (err) {
      console.error('Error uploading hero image:', err);
      setError(err?.message || 'Failed to upload hero image');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleToggleActive = async (imageId, currentStatus) => {
    try {
      const { error } = await supabase
        ?.from('hero_images')
        ?.update({ is_active: !currentStatus })
        ?.eq('id', imageId);

      if (error) throw error;

      setSuccess('Image status updated successfully!');
      await fetchHeroImages();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating image status:', err);
      setError('Failed to update image status');
    }
  };

  const handleDelete = async (imageId, imageUrl) => {
    if (!confirm('Are you sure you want to delete this hero image?')) return;

    try {
      // Extract file path from URL
      const urlParts = imageUrl?.split('/hero-images/');
      const filePath = urlParts?.[1];

      // Delete from storage
      if (filePath) {
        const { error: storageError } = await supabase?.storage
          ?.from('hero-images')
          ?.remove([filePath]);

        if (storageError) console.error('Storage delete error:', storageError);
      }

      // Delete from database
      const { error: dbError } = await supabase
        ?.from('hero_images')
        ?.delete()
        ?.eq('id', imageId);

      if (dbError) throw dbError;

      setSuccess('Hero image deleted successfully!');
      await fetchHeroImages();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting hero image:', err);
      setError('Failed to delete hero image');
    }
  };

  const handleReorder = async (imageId, direction) => {
    try {
      const currentIndex = heroImages?.findIndex(img => img?.id === imageId);
      const currentImage = heroImages?.[currentIndex];
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (targetIndex < 0 || targetIndex >= heroImages?.length) return;

      const targetImage = heroImages?.[targetIndex];

      // Swap display orders
      await supabase
        ?.from('hero_images')
        ?.update({ display_order: targetImage?.display_order })
        ?.eq('id', currentImage?.id);

      await supabase
        ?.from('hero_images')
        ?.update({ display_order: currentImage?.display_order })
        ?.eq('id', targetImage?.id);

      await fetchHeroImages();
    } catch (err) {
      console.error('Error reordering images:', err);
      setError('Failed to reorder images');
    }
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Icon name="Image" size={24} className="text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">Hero Slideshow Images</h2>
            <p className="text-white/60 text-sm">Manage hero section background slideshow images</p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="mb-6">
        <div className="border-2 border-dashed border-white/30 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
          <input
            type="file"
            id="hero-image-upload"
            accept="image/*"
            onChange={handleFileUpload}
            disabled={isUploading}
            className="hidden"
          />
          <label
            htmlFor="hero-image-upload"
            className={`cursor-pointer inline-flex flex-col items-center ${isUploading ? 'opacity-50' : ''}`}
          >
            <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mb-3">
              <Icon name={isUploading ? "Loader" : "Upload"} size={32} className={`text-purple-400 ${isUploading ? 'animate-spin' : ''}`} />
            </div>
            <p className="text-white font-medium mb-1">
              {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Hero Image'}
            </p>
            <p className="text-white/60 text-sm">
              Click to browse or drag and drop (Max 5MB)
            </p>
          </label>
        </div>

        {/* Upload Progress Bar */}
        {isUploading && (
          <div className="mt-4">
            <div className="w-full bg-white/10 rounded-full h-2">
              <div
                className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={20} className="text-red-400" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-4 bg-green-500/10 border border-green-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={20} className="text-green-400" />
            <p className="text-green-400 text-sm">{success}</p>
          </div>
        </div>
      )}

      {/* Images List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-purple-400 border-t-transparent" />
          <p className="text-white/60 text-sm mt-2">Loading images...</p>
        </div>
      ) : heroImages?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="ImageOff" size={48} className="text-white/30 mx-auto mb-2" />
          <p className="text-white/60">No hero images uploaded yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {heroImages?.map((image, index) => (
            <div
              key={image?.id}
              className="bg-white/5 rounded-lg p-4 flex items-center gap-4 hover:bg-white/10 transition-colors"
            >
              {/* Image Preview */}
              <img
                src={image?.image_url}
                alt={`Hero image ${index + 1}`}
                className="w-24 h-16 object-cover rounded-lg"
              />

              {/* Image Info */}
              <div className="flex-1">
                <p className="text-white font-medium text-sm">
                  Image {index + 1}
                </p>
                <p className="text-white/60 text-xs">
                  Order: {image?.display_order} • {image?.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {/* Reorder Buttons */}
                <button
                  onClick={() => handleReorder(image?.id, 'up')}
                  disabled={index === 0}
                  className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <Icon name="ChevronUp" size={16} className="text-white" />
                </button>
                <button
                  onClick={() => handleReorder(image?.id, 'down')}
                  disabled={index === heroImages?.length - 1}
                  className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <Icon name="ChevronDown" size={16} className="text-white" />
                </button>

                {/* Toggle Active */}
                <button
                  onClick={() => handleToggleActive(image?.id, image?.is_active)}
                  className={`p-2 rounded-lg transition-colors ${
                    image?.is_active
                      ? 'bg-green-500/20 hover:bg-green-500/30' :'bg-gray-500/20 hover:bg-gray-500/30'
                  }`}
                  title={image?.is_active ? 'Deactivate' : 'Activate'}
                >
                  <Icon
                    name={image?.is_active ? 'Eye' : 'EyeOff'}
                    size={16}
                    className={image?.is_active ? 'text-green-400' : 'text-gray-400'}
                  />
                </button>

                {/* Delete Button */}
                <button
                  onClick={() => handleDelete(image?.id, image?.image_url)}
                  className="p-2 bg-red-500/20 rounded-lg hover:bg-red-500/30 transition-colors"
                  title="Delete"
                >
                  <Icon name="Trash2" size={16} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Notice */}
      <div className="mt-6 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-400">
            <p className="font-medium mb-1">Hero Slideshow Tips:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-400/80">
              <li>Images will cycle automatically in the hero section background</li>
              <li>Use high-quality images (1920x1080 or larger recommended)</li>
              <li>Active images will appear in the slideshow</li>
              <li>Reorder images to change slideshow sequence</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroImageManager;